// covered-by: frontends/carrie/src/ask-v2/routes/__tests__/authLayout.test.tsx
// eslint-disable-next-line import/named
import { createRoute, redirect, useRouter } from "@tanstack/react-router";

import PendingComponent from "../../components/PendingComponent";
import { SIDEBAR_STORAGE_NAME } from "../../components/sidebar/SidebarProvider";
import { loginRoute } from "../../routes/login/route";
import { rootRoute } from "../../routes/root/route";
import { useAppSelector } from "../../utils/hooks";
import { logoutUrl } from "../../utils/logoutUrl";
import { setDrawerOpen } from "../../utils/slices/ui";
import { store } from "../../utils/store";
import { AskV2Layout } from "../shell/AskV2Layout";

// why: reading storage throws SecurityError rather than returning null when it is
// blocked (private mode, third-party-cookie blocking, sandboxed iframe). beforeLoad
// runs on every route resolution, so an unguarded read white-screens the route
// instead of falling back to the default drawer preference. Mirrors the branch route.
function readLocalStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Pathless authenticated layout for Ask V2. Reuses login, not PunttLayout —
 * the tickets sidebar must not appear on ask.puntt.ai.
 */
export const askV2AuthLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "ask-v2-authenticated",
  beforeLoad({ location }) {
    const { value: user } = store.getState().auth;
    if (user == null) {
      throw redirect({
        to: loginRoute.to,
        search: {
          redirect: location.pathname,
        },
      });
    }

    store.dispatch(
      setDrawerOpen({
        app:
          (readLocalStorage(`app_${SIDEBAR_STORAGE_NAME}`) ?? "true") ===
          "true",
        comments:
          (readLocalStorage(`comments_${SIDEBAR_STORAGE_NAME}`) ?? "true") ===
          "true",
      }),
    );
  },
  component: AskV2AuthLayout,
  pendingComponent: PendingComponent,
});

export function AskV2AuthLayout() {
  const router = useRouter();
  const user = useAppSelector((state) => state.auth.value);

  // why: beforeLoad only runs on navigation — Account → Log out clears Redux
  // without rematching routes, so the layout must hard-redirect like classic AuthLayout
  if (user == null && router.state.location.pathname !== loginRoute.to) {
    location.assign(logoutUrl(router.basepath));
    return null;
  }

  return <AskV2Layout />;
}
